import type { ReactNode } from 'react';
import { router, usePage } from '@inertiajs/react';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Wordmark } from '@/components/Wordmark';
import { Button } from '@/components/ui/Button';

type AdminLayoutProps = {
  children?: ReactNode;
};

const onSignOut = () => {
  router.delete('/sign_out');
};

/**
 * Admin dashboard shell: a compact chrome header (the `Wordmark` — which
 * always links `/`, i.e. the admin inbox root on this subdomain — the
 * signed-in admin's email, the `ThemeToggle`, and sign-out) over a tokenized
 * content area. Unlike `PublicLayout`, this favors information density over
 * marketing whitespace — the admin is scanned and operated, not read.
 */
export const AdminLayout = ({ children }: AdminLayoutProps) => {
  const { currentAdmin } = usePage().props;

  return (
    <div className="bg-background min-h-screen">
      <header className="bg-surface/92 border-line-soft sticky top-0 z-40 border-b backdrop-blur">
        <div className="mx-auto flex h-16 max-w-[1120px] items-center justify-between gap-3 px-5 md:px-10">
          <Wordmark className="min-w-0 shrink whitespace-nowrap" />

          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            {currentAdmin ? (
              <span className="text-ink-soft hidden max-w-[200px] truncate text-sm md:inline">
                {currentAdmin.email}
              </span>
            ) : null}
            <ThemeToggle />
            {/* The wrapper (not the Button itself) toggles visibility — Button
                always renders its own `inline-flex`, which would otherwise
                compete with an unprefixed `hidden` at the same specificity. */}
            <span className="hidden sm:inline-block">
              <Button variant="ghost" onClick={onSignOut}>
                Sign out
              </Button>
            </span>
            <button
              type="button"
              onClick={onSignOut}
              aria-label="Sign out"
              className="hover:bg-surface text-ink-soft hover:text-ink grid size-11 place-items-center rounded-[3px] sm:hidden"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.6}
                className="size-[19px]"
                aria-hidden="true"
              >
                <path
                  d="M9 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h3M16 16l4-4-4-4M20 12H9"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1120px] px-5 py-8 md:px-10 md:py-10">{children}</main>
    </div>
  );
};
